import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Mail, Loader2 } from 'lucide-react';

// Map API error codes to translation keys
const errorKeyMap: Record<string, string> = {
  INVALID_TOKEN: 'invalidToken',
  TOKEN_EXPIRED: 'invalidToken',
  VALIDATION_ERROR: 'invalidToken',
};

export function VerifyEmail() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, verifyEmail, resendVerification } = useAuth();

  const [status, setStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  const token = searchParams.get('token');

  useEffect(() => {
    if (token && status === 'idle') {
      setStatus('verifying');
      verifyEmail(token).then((result) => {
        if (result.success) {
          setStatus('success');
          setTimeout(() => navigate('/dashboard'), 2000);
        } else {
          setStatus('error');
          const errorKey = errorKeyMap[result.error || ''] || 'invalidToken';
          setError(t(`errors.${errorKey}`));
        }
      });
    }
  }, [token, status, verifyEmail, navigate, t]);

  const handleResend = async () => {
    setResendStatus('sending');
    const result = await resendVerification();
    if (result.success) {
      setResendStatus('sent');
    } else {
      setResendStatus('idle');
    }
  };

  // If we have a token, show verification status
  if (token) {
    return (
      <div className="container flex items-center justify-center min-h-[calc(100vh-8rem)]">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <CardTitle className="text-2xl">
              {status === 'verifying' && t('verifyEmail.checking')}
              {status === 'success' && t('verifyEmail.success')}
              {status === 'error' && t('errors.invalidToken')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            {status === 'verifying' && <Loader2 className="h-12 w-12 animate-spin text-primary" />}
            {status === 'success' && <CheckCircle className="h-12 w-12 text-green-500" />}
            {status === 'error' && (
              <>
                <p className="text-destructive">{error}</p>
                <Button onClick={() => navigate('/login')}>
                  {t('forgotPassword.backToLogin')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Otherwise show the "check your email" message
  return (
    <div className="container flex items-center justify-center min-h-[calc(100vh-8rem)]">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <Mail className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t('verifyEmail.title')}</CardTitle>
          <CardDescription>{t('verifyEmail.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('verifyEmail.description')}</p>

          {user && (
            <p className="text-sm font-medium">{user.email}</p>
          )}

          <Button
            variant="outline"
            onClick={handleResend}
            disabled={resendStatus !== 'idle'}
          >
            {resendStatus === 'sending' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {resendStatus === 'sent' ? t('verifyEmail.resent') : t('verifyEmail.resend')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
